import {MDIcon, MDIconSize} from '@mintdata-wsp/common/components/icon/MDIcon';
import {MDIcons} from '@mintdata-wsp/common/components/icon/MDIcons';
import {DesignVersionFilterType} from '@mintdata-wsp/common/state/ui/visualDesigner/DesignVersionsState';
import {
    DesignVersionDiff,
    DesignVersionDiffChanges,
    DesignVersionDiffObjectType,
    DesignVersionDiffText
} from '@mintdata-wsp/wsp/controllers/visualDesigner/designState/DesignVersionsController';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {Column} from '@mintdata-wsp/wsp/ui/components/forms/Column';
import {Row} from '@mintdata-wsp/wsp/ui/components/forms/Row';
import {DesignVersionsFilterIcon} from '@mintdata-wsp/wsp/ui/views/visualDesigner/surface/panels/versions/VersionsViewHeader';
import bind from 'bind-decorator';
import * as classnames from 'classnames/bind';
import {observer} from 'mobx-react';
import * as React from 'react';

const styles = require('./VersionsViewDiff.less');
const cx = classnames.bind(styles);

@observer
export class VersionsViewDiff extends React.Component<any, any>
{
    public render():JSX.Element
    {
        const dvc = controllers.ui.designVersions;

        const diff = dvc.getDiff();

        return (
            <Column className={cx('linesWrapper')}>

                {diff.map(this._renderDiffLine)}

                <div className={cx('line', 'grow')}>
                    <Row className={cx('side', 'left')} />
                    <Row className={cx('side', 'right')} />
                </div>

            </Column>
        );
    }

    @bind
    private _renderDiffLine(line:DesignVersionDiff, index:number, diff:DesignVersionDiff[]):JSX.Element
    {
        const lineTypeClass = 'lineType-' + line.objectType;
        const actionClass = 'action-' + line.actionType;

        const dvc = controllers.ui.designVersions;

        const isSheet = line.objectType === DesignVersionDiffObjectType.SHEET;
        const isPage = line.objectType === DesignVersionDiffObjectType.PAGE;
        const isGroup = line.objectType === DesignVersionDiffObjectType.GROUP;

        const isShowSheet = dvc.getFilterState(DesignVersionFilterType.SHEET);
        const isShowPage = dvc.getFilterState(DesignVersionFilterType.PAGE);

        if( isSheet && !isShowSheet ) return;
        if( isPage && !isShowPage ) return;

        let isNeedSeparator = false;

        if( (isSheet || isPage) && isShowSheet )
        {
            isNeedSeparator = index > 0 && line.objectType !== diff[index - 1].objectType;
        }

        let isViewChildren = line.items && line.items.length > 0;

        if( isGroup && !dvc.getFilterState(line.filterType) )
        {
            isViewChildren = false;
        }

        return (
            <div>
                {
                    isNeedSeparator &&
                    <div className={cx('separator')} />
                }
                <div className={cx('line', actionClass, lineTypeClass)}>
                    {this._renderChanges(line, line.left, 'left')}
                    {this._renderChanges(line, line.right, 'right')}
                </div>
                {
                    isViewChildren &&
                    <div className={cx('items', lineTypeClass)}>
                        {line.items.map(this._renderDiffLine)}
                    </div>
                }
            </div>
        );
    }

    @bind
    private _renderChanges(item:DesignVersionDiff, changes:DesignVersionDiffChanges, side:string):JSX.Element
    {
        let renderer:(item:DesignVersionDiff, changes:DesignVersionDiffChanges) => JSX.Element = () =>
        {
            return null;
        };

        if( changes )
        {
            if( item.objectType === DesignVersionDiffObjectType.SHEET ||
                item.objectType === DesignVersionDiffObjectType.PAGE )
            {
                renderer = this._renderSheetOrPage;
            }
            else if( item.objectType === DesignVersionDiffObjectType.GROUP )
            {
                renderer = this._renderGroup;
            }
            else if( item.objectType === DesignVersionDiffObjectType.ITEM )
            {
                renderer = this._renderItem;
            }
        }

        return (
            <Row className={cx('side', side)}>
                {renderer(item, changes)}
            </Row>
        );
    }

    @bind
    private _renderSheetOrPage(item:DesignVersionDiff, changes:DesignVersionDiffChanges):JSX.Element
    {
        const isSheet = item.objectType === DesignVersionDiffObjectType.SHEET;

        const objectClass = 'object-' + (isSheet ? 'sheet' : 'page');

        const isClosedLevel0Class = Math.random() > .5 ? 'isClosedLevel0' : 'isOpenedLevel0';

        return (
            <Row
                className={cx(objectClass)}
            >
                <MDIcon
                    className={cx('icon', 'closeLevel0', isClosedLevel0Class)}
                    name={MDIcons.common.ArrowInBox}
                    size={MDIconSize.medium20}
                />
                <MDIcon
                    className={cx('icon')}
                    name={isSheet ? MDIcons.common.Sheet : MDIcons.common.Page}
                    size={MDIconSize.medium20}
                />
                <Row
                    className={cx('name')}
                >
                    {this._renderTexts(changes.texts)}
                </Row>
            </Row>
        );
    }

    @bind
    private _renderGroup(item:DesignVersionDiff, changes:DesignVersionDiffChanges):JSX.Element
    {
        let isGroupOpened = false;
        let isGroupClosed = false;

        if( item.objectType === DesignVersionDiffObjectType.GROUP )
        {
            const dvc = controllers.ui.designVersions;

            if( item.filterType === DesignVersionFilterType.COMPONENTS )
                isGroupOpened = dvc.getFilterState(DesignVersionFilterType.COMPONENTS);
            else if( item.filterType === DesignVersionFilterType.POSITIONS )
                isGroupOpened = dvc.getFilterState(DesignVersionFilterType.POSITIONS);
            else if( item.filterType === DesignVersionFilterType.NOTES )
                isGroupOpened = dvc.getFilterState(DesignVersionFilterType.NOTES);
            else if( item.filterType === DesignVersionFilterType.EVENT_BINDINGS )
                isGroupOpened = dvc.getFilterState(DesignVersionFilterType.EVENT_BINDINGS);
            else if( item.filterType === DesignVersionFilterType.PROP_BINDINGS )
                isGroupOpened = dvc.getFilterState(DesignVersionFilterType.PROP_BINDINGS);
            else if( item.filterType === DesignVersionFilterType.NAMED_RANGES )
                isGroupOpened = dvc.getFilterState(DesignVersionFilterType.NAMED_RANGES);
            else if( item.filterType === DesignVersionFilterType.CELLS )
                isGroupOpened = dvc.getFilterState(DesignVersionFilterType.CELLS);

            isGroupClosed = !isGroupOpened;
        }

        return (
            <Row
                className={cx('object-group', {isGroupOpened, isGroupClosed})}
            >
                <Row
                    className={cx('name')}
                    onClick={(e) => this._onClickName(e, item)}
                >
                    <MDIcon
                        className={cx('icon', {isGroupOpened, isGroupClosed})}
                        name={MDIcons.common.ChevronRight}
                        size={MDIconSize.medium20}
                    />
                    {changes.groupName}
                </Row>
                <div className={cx('groupLine')} />
            </Row>
        );
    }

    @bind
    private _renderItem(item:DesignVersionDiff, changes:DesignVersionDiffChanges):JSX.Element
    {
        const iconName = DesignVersionsFilterIcon[item.filterType];

        const lineNumber = item.lineNumber < 10 ? '0' + item.lineNumber : item.lineNumber;

        return (
            <Row
                className={cx('object-item')}
            >
                <div
                    className={cx('lineNumber')}
                >
                    {lineNumber}
                </div>

                <MDIcon
                    className={cx('icon')}
                    name={iconName}
                    size={MDIconSize.medium16}
                />

                <div
                    className={cx('range')}
                >
                    {changes.range}
                </div>

                <div
                    className={cx('propName')}
                >
                    {changes.propName}
                </div>

                <Row
                    className={cx('textsWrapper')}
                >
                    {this._renderTexts(changes.texts)}
                </Row>

            </Row>
        );
    }

    @bind
    private _onClickName(event:React.MouseEvent<HTMLElement>, item:DesignVersionDiff)
    {
        event.preventDefault();
        event.stopPropagation();

        if( item.objectType === DesignVersionDiffObjectType.GROUP )
        {
            controllers.ui.designVersions.toggleFilter(item.filterType);
        }
    }

    @bind
    private _renderTexts(texts:DesignVersionDiffText[]):JSX.Element
    {
        return (
            <div
                className={cx('texts')}
            >
                {texts.map((line:DesignVersionDiffText) =>
                {
                    const changeClass = 'changeType-' + line.type;

                    return (
                        <span className={cx('text', changeClass)}>
                            {line.text}
                        </span>
                    );

                    // return (
                    //     <span>
                    //         <span className={cx('text', changeClass)}>
                    //             {line.text}
                    //         </span>
                    //         &nbsp;
                    //     </span>
                    // );
                })}
            </div>
        );
    }

    // private _renderItem(item:DesignVersionDiff):JSX.Element
    // {
    //     return (
    //         <Row>
    //             {item.lineNumber}
    //         </Row>
    //     );
    // }

}
